import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';
import { db, COLLECTIONS } from '../firebase/config';
import { 
  collection, 
  query, 
  where, 
  getDocs,
  Timestamp
} from 'firebase/firestore';
import { 
  BarChart3,
  Users,
  FileText,
  Clock,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  XCircle,
  Activity,
  Target,
  Package,
  Calendar,
  ArrowUp,
  ArrowDown,
  Loader2
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Area,
  AreaChart
} from 'recharts';

const ManagerDashboard = () => {
  const { currentUser } = useAuth();
  const { tasks = [], users = [], projects = [] } = useData();
  
  // הוסף loading state
  const [loading, setLoading] = useState(true);
  const [sopInstances, setSopInstances] = useState([]);
  const [sopTemplates, setSopTemplates] = useState([]);
  const [stats, setStats] = useState({
  activeSOPs: 0,
  completionRate: 0,
  avgCompletionTime: 0,
  overdueProcesses: 0,
  totalTasks: 0,
  completedTasks: 0,
  totalEmployees: 0,
  activeEmployees: 0
});
const [performanceData, setPerformanceData] = useState([]);
const [taskStatusData, setTaskStatusData] = useState([]);
const [departmentData, setDepartmentData] = useState([]);

useEffect(() => {
  const loadData = async () => {
    setLoading(true);
    try {
      // חישוב נתונים מהמערכת
      const activeTasks = tasks.filter(task => task.status !== 'completed');
      const completedTasks = tasks.filter(task => task.status === 'completed');
      const overdueTasks = tasks.filter(task => {
        if (!task.dueDate) return false;
        const dueDate = task.dueDate.toDate ? task.dueDate.toDate() : new Date(task.dueDate);
        return dueDate < new Date() && task.status !== 'completed';
      });

      // חישוב נתוני עובדים
      const activeUsers = users.filter(user => user.isActive !== false);
      
      // חישוב אחוז השלמה
      const completionRate = tasks.length > 0 
        ? Math.round((completedTasks.length / tasks.length) * 100)
        : 0;

      setStats({
        activeSOPs: activeTasks.length,
        completionRate: completionRate,
        avgCompletionTime: 4.5,
        overdueProcesses: overdueTasks.length,
        totalTasks: tasks.length,
        completedTasks: completedTasks.length,
        totalEmployees: users.length,
        activeEmployees: activeUsers.length
      });

      // חישוב נתונים לגרפים
      await calculatePerformanceData();
      calculateTaskStatusData(activeTasks.length, completedTasks.length, overdueTasks.length);
      calculateDepartmentData();

    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };
  
  loadData();
}, [currentUser, tasks, users]);
const loadDashboardData = async () => {
  setLoading(true);
  try {
    // חישוב נתונים מהמערכת
    const activeTasks = tasks.filter(task => task.status !== 'completed');
    const completedTasks = tasks.filter(task => task.status === 'completed');
    const overdueTasks = tasks.filter(task => {
      if (!task.dueDate) return false;
      const dueDate = task.dueDate.toDate ? task.dueDate.toDate() : new Date(task.dueDate);
      return dueDate < new Date() && task.status !== 'completed';
    });

    // חישוב נתוני עובדים
    const activeUsers = users.filter(user => user.isActive !== false);
    
    // חישוב אחוז השלמה
    const completionRate = tasks.length > 0 
      ? Math.round((completedTasks.length / tasks.length) * 100)
      : 0;

    setStats({
      activeSOPs: activeTasks.length,
      completionRate: completionRate,
      avgCompletionTime: 4.5, // בינתיים
      overdueProcesses: overdueTasks.length,
      totalTasks: tasks.length,
      completedTasks: completedTasks.length,
      totalEmployees: users.length,
      activeEmployees: activeUsers.length
    });

    // חישוב נתונים לגרפים
    await calculatePerformanceData();
    calculateTaskStatusData(activeTasks.length, completedTasks.length, overdueTasks.length);
    calculateDepartmentData();

  } catch (error) {
    console.error('Error loading dashboard data:', error);
  } finally {
    setLoading(false);
  }
};
const calculatePerformanceData = async () => {
  const monthsData = [];
  const now = new Date();
  
  for (let i = 5; i >= 0; i--) {
    const monthDate = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const monthName = monthDate.toLocaleDateString('he-IL', { month: 'long' });
    
    const monthTasks = tasks.filter(task => {
      if (!task.completedAt) return false;
      const completedDate = task.completedAt.toDate ? task.completedAt.toDate() : new Date(task.completedAt);
      return completedDate.getMonth() === monthDate.getMonth() && 
             completedDate.getFullYear() === monthDate.getFullYear();
    });
    
    monthsData.push({
      name: monthName,
      השלמות: monthTasks.length,
      יעד: 10
    });
  }
  
  setPerformanceData(monthsData);
};

const calculateTaskStatusData = (active, completed, overdue) => {
  const total = active + completed + overdue;
  if (total === 0) {
    setTaskStatusData([
      { name: 'הושלמו', value: 0, color: '#10b981' },
      { name: 'בתהליך', value: 0, color: '#3b82f6' },
      { name: 'באיחור', value: 0, color: '#ef4444' }
    ]);
    return;
  }
  
  setTaskStatusData([
    { name: 'הושלמו', value: completed, color: '#10b981' },
    { name: 'בתהליך', value: active - overdue, color: '#3b82f6' },
    { name: 'באיחור', value: overdue, color: '#ef4444' }
  ]);
};

const calculateDepartmentData = () => {
  // בינתיים נחזיר מערך ריק
  setDepartmentData([]);
};
  // נתונים לדוגמה - בינתיים
 
  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">לוח בקרה למנהלים</h1>
        <p className="text-gray-600 mt-2">מעקב אחר ביצועי תהליכי SOP וצוות</p>
      </div>

      {/* כרטיסי סטטיסטיקה */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">תהליכי SOP פעילים</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{stats.activeSOPs}</p>
              <p className="text-sm text-green-600 mt-2 flex items-center">
                <ArrowUp className="h-4 w-4 mr-1" />
                +15% מהחודש שעבר
              </p>
            </div>
            <div className="p-3 bg-blue-100 rounded-full">
              <FileText className="h-8 w-8 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">אחוז השלמה</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{stats.completionRate}%</p>
              <p className="text-sm text-green-600 mt-2 flex items-center">
                <ArrowUp className="h-4 w-4 mr-1" />
                +5% מהיעד
              </p>
            </div>
            <div className="p-3 bg-green-100 rounded-full">
              <Target className="h-8 w-8 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">זמן השלמה ממוצע</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{stats.avgCompletionTime} ימים</p>
              <p className="text-sm text-gray-500 mt-2">
                יעד: 5 ימים
              </p>
            </div>
            <div className="p-3 bg-yellow-100 rounded-full">
              <Clock className="h-8 w-8 text-yellow-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">תהליכים באיחור</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{stats.overdueProcesses}</p>
              <p className="text-sm text-red-600 mt-2 flex items-center">
                <AlertCircle className="h-4 w-4 mr-1" />
                דורש טיפול
              </p>
            </div>
            <div className="p-3 bg-red-100 rounded-full">
              <XCircle className="h-8 w-8 text-red-600" />
            </div>
          </div>
        </div>
      </div>

      {/* גרפים */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* גרף ביצועים לאורך זמן */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">ביצועי השלמה לאורך זמן</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={performanceData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="השלמות" 
                stroke="#3b82f6" 
                strokeWidth={2}
                dot={{ fill: '#3b82f6' }}
              />
              <Line 
                type="monotone" 
                dataKey="יעד" 
                stroke="#ef4444" 
                strokeDasharray="5 5"
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* גרף עוגה - סטטוס משימות */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">התפלגות סטטוס משימות</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={taskStatusData}
                cx="50%"
                cy="50%"
                labelLine={false}
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
              >
                {taskStatusData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* טבלת ביצועי מחלקות */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">ביצועי מחלקות</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  מחלקה
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  סה"כ משימות
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  הושלמו
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  אחוז השלמה
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  סטטוס
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {departmentData.map((dept) => (
                <tr key={dept.name}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {dept.name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {dept.משימות}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {dept.השלמות}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div className="flex items-center">
                      <span className="mr-2">{dept.אחוז}%</span>
                      <div className="w-20 bg-gray-200 rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full ${
                            dept.אחוז >= 85 ? 'bg-green-500' : 
                            dept.אחוז >= 70 ? 'bg-yellow-500' : 
                            'bg-red-500'
                          }`}
                          style={{ width: `${dept.אחוז}%` }}
                        />
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {dept.אחוז >= 85 ? (
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                        מצוין
                      </span>
                    ) : dept.אחוז >= 70 ? (
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                        תקין
                      </span>
                    ) : (
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                        דורש שיפור
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ManagerDashboard;